/**
 * ログインページ
 *************************************************/
import React, { FC, useEffect, useState } from 'react';
import Form from 'semantic-ui-react/dist/commonjs/collections/Form/Form';
import Grid from 'semantic-ui-react/dist/commonjs/collections/Grid/Grid';
import Header from 'semantic-ui-react/dist/commonjs/elements/Header/Header';
import Segment from 'semantic-ui-react/dist/commonjs/elements/Segment/Segment';
import { Image, Message } from 'semantic-ui-react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { endLoading, startLoading } from 'modules/commonModule';

type FormData = {
  userId: string;
  password: string;
};
const LoginPage: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { handleSubmit, errors, control } = useForm<FormData>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      userId: '',
      password: '',
    },
  });

  /**
   * ログイン処理
   */
  const onSubmit = handleSubmit(async (data) => {
    try {
      dispatch(startLoading());

      // trim関数で文字列の空白を除去
      const userId = data.userId.trim();
      const password = data.password.trim();

      if (userId && password) {
        let user = await Auth.signIn(userId, password);

        if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          await Auth.completeNewPassword(
            user, // the Cognito User Object
            password, // the new password
            { email: 'apply@freecraft.co.jp' },
          );

          // カスタム属性（email）追加後、ユーザー情報を再取得
          user = await Auth.signIn(userId, password);
        }

        if (user.attributes['custom:status'] && user.attributes['custom:role'] !== 'tester') {
          setErrorMessage('適性検査試験は受講済みです');
        } else if (user.attributes['custom:role'] === 'admin') {
          history.push('/admin/score');
        } else {
          history.push('/notice', { userId: user.username });
        }
      }
    } catch (e) {
      console.log(e);
      if (e.code === 'NotAuthorizedException') {
        // パスワードが間違っている場合
        setErrorMessage('パスワードが間違っています');
      } else {
        // ユーザーが存在しない場合
        setErrorMessage('ユーザーIDが間違っています');
      }
    } finally {
      dispatch(endLoading());
    }
  });

  // 前回ログインした際の認証情報をクリアする
  useEffect(() => {
    Auth.signOut();
  }, []);

  return (
    <Grid textAlign="center" style={{ height: '100vh' }} verticalAlign="middle">
      <Grid.Column style={{ maxWidth: 450 }}>
        <Image src={`${process.env.PUBLIC_URL}/fcc-logo.png`} size="small" centered />
        <Header as="h1" size="large" style={{ lineHeight: 1.6, marginBottom: '100px' }} textAlign="center">
          株式会社フリークラフト
          <br />
          適性検査試験
        </Header>
        <Form size="large" onSubmit={onSubmit} error={!!(Object.keys(errors).length > 0 || errorMessage)}>
          <Message
            error
            content={() => {
              return (
                <>
                  {errors.userId && <p data-cy="err-message-userId">{errors.userId.message}</p>}
                  {errors.password && <p data-cy="err-message-password">{errors.password.message}</p>}
                  {errorMessage && <p data-cy="err-message-other">{errorMessage}</p>}
                </>
              );
            }}
            data-cy="err-message-area"
          />
          <Segment stacked>
            <Controller
              as={
                <Form.Input
                  id="userId"
                  fluid
                  size="large"
                  icon="user"
                  iconPosition="left"
                  placeholder="ユーザーID"
                  data-cy="userId-input"
                />
              }
              control={control}
              name="userId"
              rules={{ required: 'ユーザーIDを入力してください' }}
              error={!!errors.userId}
            />
            <Controller
              as={
                <Form.Input
                  id="password"
                  fluid
                  size="large"
                  icon="lock"
                  iconPosition="left"
                  placeholder="パスワード"
                  data-cy="password-input"
                />
              }
              control={control}
              name="password"
              rules={{ required: 'パスワードを入力してください' }}
              helperText={errors.password ? errors.password.message : ''}
              error={!!errors.password}
            />

            <Form.Button fluid size="big" primary data-cy="login-button">
              ログイン
            </Form.Button>
          </Segment>
        </Form>
      </Grid.Column>
    </Grid>
  );
};
export default LoginPage;
